<script setup lang="ts">
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import FormSelect from '@/components/Elements/Forms/FormSelect.vue'
import { useI18n } from 'vue-i18n'
import { setLocale, SUPPORTED_LOCALES, LOCALE_NAMES, LOCALE_FLAG_KEY, type SupportedLocale } from '@/locales'
import { usePlayerFlagsStore } from '@/stores/playerFlagsStore'
import { ref, computed } from 'vue'

const { t, locale } = useI18n()
const playerFlagsStore = usePlayerFlagsStore()
const isChanging = ref(false)

const localeOptions = computed(() =>
  [...SUPPORTED_LOCALES]
    .sort((a, b) => LOCALE_NAMES[a].localeCompare(LOCALE_NAMES[b]))
    .map((loc) => ({ value: loc, label: `${LOCALE_FLAGS[loc]}\u2002${LOCALE_NAMES[loc]}` }))
)

const LOCALE_FLAGS: Record<SupportedLocale, string> = {
  'en-us': '🇺🇸',
  'en-gb': '🇬🇧',
  'es-es': '🇪🇸',
  'pt-br': '🇧🇷',
  'bg-bg': '🇧🇬',
  'pl-pl': '🇵🇱',
  'fr-fr': '🇫🇷',
  'it-it': '🇮🇹',
  'de-de': '🇩🇪',
  'ru-ru': '🇷🇺',
  'zh-cn': '🇨🇳',
  'tr-tr': '🇹🇷',
  'ko-kr': '🇰🇷',
  'cs-cz': '🇨🇿',
  'hu-hu': '🇭🇺',
  'ro-ro': '🇷🇴',
  'uk-ua': '🇺🇦',
  'nl-nl': '🇳🇱'
}

async function changeLanguage(val: string | number) {
  const newLocale = val as SupportedLocale

  if (newLocale === locale.value) return

  isChanging.value = true
  try {
    await setLocale(newLocale)
    await playerFlagsStore.setFlag(LOCALE_FLAG_KEY, newLocale)
  } finally {
    isChanging.value = false
  }
}
</script>

<template>
  <StandardCard :title="t('settings.language.title')">
    <div class="flex justify-between items-center">
      <div>
        {{ t('settings.language.description') }}
      </div>
      <div class="flex items-center">
        <FormSelect
          :modelValue="locale"
          @update:modelValue="changeLanguage"
          :options="localeOptions"
          :disabled="isChanging"
          class="min-w-40"
        />
      </div>
    </div>
  </StandardCard>
</template>

<style scoped lang="scss">
</style>
